// ===============================================================
// deviceSecurity.js — app-scoped device registration (V1)
// Collects legal metadata only; server hashes deviceId for storage.
// ===============================================================

import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";

const WEB_DEVICE_ID_KEY = "livora_web_device_id";

function createWebDeviceId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `web_${crypto.randomUUID()}`;
  }
  return `web_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/**
 * Collect device fields allowed for registerDevice (no IMEI/MAC/contacts/etc.).
 * @returns {Promise<{ deviceId: string, platform: string, model: string, manufacturer: string, osVersion: string, isVirtual: boolean }>}
 */
export async function collectDeviceInfo() {
  const platform = Capacitor.getPlatform();
  let deviceId = "";
  let model = "";
  let manufacturer = "";
  let osVersion = "";
  let isVirtual = false;

  try {
    if (platform === "android" || platform === "ios") {
      const idResult = await Device.getId();
      deviceId = String(idResult?.identifier || "").trim();

      const info = await Device.getInfo();
      model = String(info?.model || "").trim();
      manufacturer = String(info?.manufacturer || "").trim();
      osVersion = String(info?.osVersion || "").trim();
      isVirtual = Boolean(info?.isVirtual);
    } else {
      try {
        deviceId = String(localStorage.getItem(WEB_DEVICE_ID_KEY) || "").trim();
      } catch {
        deviceId = "";
      }
      if (!deviceId) {
        deviceId = createWebDeviceId();
        try {
          localStorage.setItem(WEB_DEVICE_ID_KEY, deviceId);
        } catch {
          /* ignore quota / private mode */
        }
      }
    }
  } catch (err) {
    console.warn("collectDeviceInfo failed:", err);
  }

  return {
    deviceId,
    platform,
    model,
    manufacturer,
    osVersion,
    isVirtual,
  };
}

/**
 * Register the current device with the server (best-effort; does not throw).
 * @returns {Promise<{ ok: boolean, skipped?: string, error?: unknown, data?: object }>}
 */
export async function registerCurrentDevice() {
  if (!auth.currentUser?.uid) {
    return { ok: false, skipped: "no_auth" };
  }

  const payload = await collectDeviceInfo();
  if (!payload.deviceId) {
    return { ok: false, skipped: "no_device_id" };
  }

  try {
    const fn = httpsCallable(functions, "registerDevice");
    const res = await fn(payload);
    return { ok: true, data: res?.data || null };
  } catch (err) {
    console.warn("registerCurrentDevice failed:", err);
    return { ok: false, error: err };
  }
}
